/**
 * Data caching utility to reduce server calls and improve performance
 */

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  expiry: number; // milliseconds
}

const CACHE_DURATIONS = {
  STATS: 5 * 60 * 1000, // 5 minutes
  LEVELS: 10 * 60 * 1000, // 10 minutes
  LESSONS: 10 * 60 * 1000, // 10 minutes
  WORDS: 30 * 60 * 1000, // 30 minutes (word meanings don't change often)
  USER_PROFILE: 5 * 60 * 1000, // 5 minutes
};

class DataCache {
  private cache: Map<string, CacheEntry<any>> = new Map();

  set<T>(key: string, data: T, duration: number = CACHE_DURATIONS.STATS): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      expiry: duration,
    });
  }

  get<T>(key: string): T | null {
    const entry = this.cache.get(key);
    if (!entry) return null;

    const isExpired = Date.now() - entry.timestamp > entry.expiry;
    if (isExpired) {
      this.cache.delete(key);
      return null;
    }

    return entry.data as T;
  }

  clear(key?: string): void {
    if (key) {
      this.cache.delete(key);
    } else {
      this.cache.clear();
    }
  }

  // Clear cache for a specific user (useful on logout/login)
  clearUserCache(userId: string): void {
    const keysToDelete: string[] = [];
    this.cache.forEach((_, key) => {
      if (key.includes(`user_${userId}`) || key.includes('stats') || key.includes('profile')) {
        keysToDelete.push(key);
      }
    });
    keysToDelete.forEach(key => this.cache.delete(key));
  }

  // Clear all cache (for recovery/reset)
  clearAll(): void {
    this.cache.clear();
  }

  // Get all cache keys (for debugging)
  getAllKeys(): string[] {
    return Array.from(this.cache.keys());
  }
}

export const dataCache = new DataCache();
export { CACHE_DURATIONS };

